/**
 * <s-quick-add> — "Choose options" popup on collection and search product cards.
 *
 * Markup (server-rendered per card, see templates/collection.liquid and templates/search.liquid):
 *   <s-quick-add>
 *     <button commandfor="DIALOG_ID" command="show-modal">Choose options</button>
 *     <s-dialog><dialog id="DIALOG_ID"> … option radios [data-qa-option="INDEX"],
 *       price [data-qa-price], image [data-qa-image], <s-product-form> with input[name="id"]
 *       and button[data-qa-submit] … </dialog></s-dialog>
 *     <script type="application/json" data-qa-variants>[{ id, options, available, price, compare, image }]</script>
 *   </s-quick-add>
 *
 * Picking options finds the matching variant and updates the hidden variant id, price, photo and
 * button state. After a successful add (s-product-form fires "product-form:added"), the popup
 * closes so the cart drawer can slide out.
 */
class SQuickAdd extends HTMLElement {
  #ac;
  #variants = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    try {
      this.#variants = JSON.parse(this.querySelector('script[data-qa-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[data-qa-option]')) this.#update();
    }, opts);

    this.addEventListener('product-form:added', () => {
      this.querySelector('dialog')?.close();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #selectedOptions() {
    const selected = [];
    this.querySelectorAll('input[data-qa-option]:checked').forEach((input) => {
      selected[Number(input.dataset.qaOption)] = input.value;
    });
    return selected;
  }

  #update() {
    const selected = this.#selectedOptions();
    const variant = this.#variants.find((v) => v.options.every((value, index) => value === selected[index]));

    const idInput = this.querySelector('input[name="id"]');
    const submit = this.querySelector('[data-qa-submit]');
    const price = this.querySelector('[data-qa-price]');
    const compare = this.querySelector('[data-qa-compare]');
    const image = this.querySelector('[data-qa-image]');

    if (idInput instanceof HTMLInputElement) idInput.value = variant ? String(variant.id) : '';

    if (submit instanceof HTMLButtonElement) {
      const canBuy = Boolean(variant?.available);
      submit.disabled = !canBuy;
      submit.textContent = canBuy
        ? submit.dataset.labelAdd
        : (variant ? submit.dataset.labelSoldOut : submit.dataset.labelUnavailable);
    }

    if (variant && price) price.textContent = variant.price;
    if (compare) {
      compare.hidden = !variant?.compare;
      compare.textContent = variant?.compare || '';
    }
    if (variant?.image && image instanceof HTMLImageElement) {
      image.removeAttribute('srcset');
      image.src = variant.image;
    }
  }
}

if (!customElements.get('s-quick-add')) customElements.define('s-quick-add', SQuickAdd);
